// FiboPromptTree/FiboPromptTree.tsx
import { Box } from "@mui/material";
import React from "react";
import { GENERAL_KEYS } from "../../../../constants/FiboConstants";
import type { FiboPromptTreeProps, JSONValue } from "../../../../models/fibo";
import { collectChanged, isObj } from "../../../../utils/fiboLabs";
import styles from "./FiboPromptTree.module.scss";
import TreeNode from "./TreeNode";

const FiboPromptTree = ({
	data,
	showPreserved = false,
	diff,
	renderPreservedIcon,
	isEditable,
	onValueChange,
}: FiboPromptTreeProps) => {
	const changed: Set<string> = React.useMemo(() => {
		if (diff?.changedPaths) return diff.changedPaths;
		if (diff) return collectChanged(diff!.previous, diff!.current ?? data, { ignoreWhenPrevNull: true });
		return new Set<string>();
	}, [diff, data]);

	const { generalEntries, otherEntries, isRootObject } = React.useMemo(() => {
		if (!isObj(data)) {
			return {
				generalEntries: [] as [string, JSONValue][],
				otherEntries: [] as [string, JSONValue][],
				isRootObject: false,
			};
		}
		const entries = Object.entries(data) as [string, JSONValue][];
		const generalEntriesUnsorted = entries.filter(([k]) => GENERAL_KEYS.has(k));
		// Sort to put edit_instruction first
		const generalEntriesSorted = [
			...generalEntriesUnsorted.filter(([k]) => k === "edit_instruction"),
			...generalEntriesUnsorted.filter(([k]) => k !== "edit_instruction"),
		];
		return {
			generalEntries: generalEntriesSorted,
			otherEntries: entries.filter(([k]) => !GENERAL_KEYS.has(k)),
			isRootObject: true,
		};
	}, [data]);

	if (!isRootObject) {
		return (
			<Box className={styles.tree}>
				<TreeNode
					v={data as JSONValue}
					path={[]}
					changed={changed}
					showPreserved={showPreserved}
					renderPreservedIcon={renderPreservedIcon}
					isEditable={isEditable}
					onValueChange={onValueChange}
				/>
			</Box>
		);
	}

	return (
		<Box className={styles.tree}>
			{generalEntries.length > 0 && (
				<TreeNode
					k="General"
					v={Object.fromEntries(generalEntries)}
					path={["__synthetic_general__"]}
					changed={changed}
					showPreserved={showPreserved}
					subtreeBases={generalEntries.map(([k]) => k)}
					renderPreservedIcon={renderPreservedIcon}
					isEditable={isEditable}
					onValueChange={onValueChange}
				/>
			)}

			{otherEntries.map(([k, v]) => (
				<TreeNode
					key={k}
					k={k}
					v={v}
					path={[k]}
					changed={changed}
					showPreserved={showPreserved}
					renderPreservedIcon={renderPreservedIcon}
					isEditable={isEditable}
					onValueChange={onValueChange}
				/>
			))}
		</Box>
	);
};

export default React.memo(FiboPromptTree);
